import {DataSplitInfo, PageRenderProvider} from "../../../../misc/pdf/parts/PageContext";
import {table} from "../../../../misc/pdf/parts/TableStyle";
import {useEffect, useMemo, useRef} from "react";
import {Page} from "../../../../misc/pdf/parts/Page";
import {
    PrepCanvasManager,
    InternalValue,
    getHingeQtyOverride, PrepValues
} from "../../../project/shopdrawing/hardware/prepCanvas/PrepCanvasManager";
import {ImperialDistance} from "../../../../misc/ImperialDistance";
import {convertPrepConfig2} from "../../../project/shopdrawing/hardware/prepCanvas/PrepManager";
import {grey} from "@mui/material/colors";
import {groupByArr} from "nate-react-api-helpers";
import {PDFHeader} from "../../../../misc/pdf/parts/PDFHeader";
import {FrameAndDoorDetail} from "./FrameDetails";
import {dbPrep, dbStrikePrep, locksetASAPrep} from "../../../project/shopdrawing/extras/PrepMenuOptions";
import {genericDistanceParser} from "../../../../misc/scroller/Table";
import {prepsGetHinge} from "../../../project/shopdrawing/extras/preps/PrepUtil";

export type HardwareDetailItem = {
    openingId: number;
    name: string;
    manager: PrepCanvasManager;
    pairSplitKey: PairSplitKey;
    cutDown: PrepConfigCutDown2 | undefined;
    hasDeadbolt: boolean;
    hasStrike: boolean;
    has161: boolean;
    isPair: boolean;
    prepDiagramName?: string;
}

export type PrepConfigCutDown2 = {
    overCutDoor: string | null;
    underCutDoor: string | null;
    underCutFrame: string | null;
}

export function poOpeningDetailLink(id: number, kind: "preps" | "frame") {
    return "detail-" + id + "-" + kind;
}

export function getHwItemDetail(frames: FrameAndDoorDetail[] = [],
                                doors: FrameAndDoorDetail[] = []) {
    let out: HardwareDetailItem[] = [];

    let pairs: {
        frame: FrameAndDoorDetail | null,
        doors: FrameAndDoorDetail[]
    }[] = [];

    frames.forEach(fr => {
        if(fr.opening !== 0) {
            pairs.push({
                frame: fr,
                doors: doors.filter(d => d.opening === fr.opening)
            })

            return;
        }

        pairs.push({
            frame: fr,
            doors: []
        });
    })

    doors.forEach(d => {
        const exist = pairs.find(p => p.doors.some(pairDr => pairDr.opening === d.opening) || p.frame?.opening === d.opening);
        if(exist) {
            exist.doors.push(d);
            return;
        }

        pairs.push({
            doors: [d],
            frame: null
        });
    })

    pairs.forEach(pair => {

        const frame = pair.frame;

        let frameNeedsDeadbolt = false;
        let frameNeedsStrike = false;
        let frameHas161 = false;
        let doorNeedsDeadbolt = false;
        let doorNeedsStrike = false;
        let doorHas161 = false;
        let hingeOverrideQty = 0;
        let hingeHeight = ImperialDistance.parse("4.5\"").universalValue();
        let height = "0'";
        let width = "0'";
        let openingId = 0;
        let openingName = "";
        let isPair = false;
        let config: Partial<PrepValues> | undefined = undefined;
        let cutDown: PrepConfigCutDown2 | undefined = undefined;

        let secondaryDoorWidth: number | undefined;

        for(let door of pair.doors) {
            openingId = door.opening;
            openingName = door.name;
            height = door.nominalHeight;
            width = door.nominalWidth;
            isPair = door.openingType === "Pair";
            doorNeedsDeadbolt = !!(door.doorHardwarePreps[dbPrep] || door.doorHardwarePreps[dbStrikePrep])
            doorNeedsStrike = !!door.doorHardwarePreps[locksetASAPrep];
            doorHas161 = !!door.doorHardwarePreps["161"];

            if(door.openingSubType === "active") {
            } else {
                secondaryDoorWidth = genericDistanceParser(door.nominalWidth).universalValue();
            }

            const prop = door.doorHardwarePreps["cutDown"];
            const cd = prop && prop.type === "cutDown" ? prop.cutDown : undefined;
            if(cd) {
                cutDown = cutDown || {
                    overCutDoor: null,
                    underCutDoor: null,
                    underCutFrame: null,
                }

                if(cd.overCut) {
                    cutDown.overCutDoor = cd.overCut;
                }

                if(cd.underCut) {
                    cutDown.underCutDoor = cd.underCut;
                }
            }

            if(!config) {
                const prop = door.doorHardwarePreps["customOffsets"];
                const offsets = prop && prop.type === "customOffsets" ? prop.customOffsets : undefined;
                if(offsets) {
                    config = convertPrepConfig2(offsets);
                }
            }

            if(hingeOverrideQty === 0) {
                hingeOverrideQty = getHingeQtyOverride(door.doorHardwarePreps, door.nominalHeight) || 0;
            }

            const hingeInfo = prepsGetHinge(door.doorHardwarePreps);
            if(hingeInfo) {
                const hingeHeightFlt = parseFloat(hingeInfo.key)
                if(!isNaN(hingeHeightFlt)) {
                    hingeHeight = ImperialDistance.fromFeetAndInches({inches: hingeHeightFlt}).universalValue();
                }
            }
        }

        // do frame second to get better values for width/is-pair
        if(frame) {
            frameNeedsDeadbolt = !!(frame.frameHardwarePreps[dbPrep] || frame.frameHardwarePreps[dbStrikePrep])
            frameNeedsStrike = !!frame.frameHardwarePreps[locksetASAPrep];
            frameHas161 = !!frame.frameHardwarePreps["161"];
            height = frame.nominalHeight;
            width = frame.nominalWidth;
            isPair = frame.openingType === "Pair";
            openingId = frame.opening;
            openingName = frame.name;

            if(hingeOverrideQty === 0) {
                hingeOverrideQty = getHingeQtyOverride(frame.frameHardwarePreps, frame.nominalHeight) || 0;
            }

            const hingeInfo = prepsGetHinge(frame.frameHardwarePreps);
            if(hingeInfo) {
                const hingeHeightFlt = parseFloat(hingeInfo.key)
                if(!isNaN(hingeHeightFlt)) {
                    hingeHeight = ImperialDistance.fromFeetAndInches({inches: hingeHeightFlt}).universalValue();
                }
            }

            const prop = frame.frameHardwarePreps["cutDown"];
            const cd = prop && prop.type === "cutDown" ? prop.cutDown : undefined;
            if(cd) {
                cutDown = cutDown || {
                    overCutDoor: null,
                    underCutDoor: null,
                    underCutFrame: null,
                }

                if(cd.underCut) {
                    cutDown.underCutFrame = cd.underCut;
                }
            }

            if(!config) {
                const prop = frame.frameHardwarePreps["customOffsets"];
                const offsets = prop && prop.type === "customOffsets" ? prop.customOffsets : undefined;
                if(offsets) {
                    config = convertPrepConfig2(offsets);
                }
            }
        }

        if(!height || !width) {
            return;
        }

        let pairSplitKey: PairSplitKey = "none";
        if(isPair && frame) {

            pairSplitKey = "equal";
            const doorSizes = frame.frameHardwarePreps["unequalPairDoorSizes"]
            if(doorSizes && doorSizes.type === "unequalPairDoorSizes") {
                let activeDoorWidth = genericDistanceParser(doorSizes.activeDoorWidth).universalValue();
                secondaryDoorWidth = genericDistanceParser(doorSizes.inactiveDoorWidth).universalValue();

                if(activeDoorWidth > secondaryDoorWidth) {
                    pairSplitKey = "active-larger";
                } else {
                    pairSplitKey = "inactive-larger";
                }
            } else {
                secondaryDoorWidth = genericDistanceParser(frame.nominalWidth).universalValue()/2;
            }
        }

        const manager = new PrepCanvasManager({
            nominalHeight: genericDistanceParser(height).universalValue(),
            nominalWidth: genericDistanceParser(width).universalValue(),
            secondaryWidth: secondaryDoorWidth,
            overrides: config,
            pair: isPair,
            overrideHingeQty: hingeOverrideQty,
            hasDeadbolt: frameNeedsDeadbolt || doorNeedsDeadbolt,
            hingeHeight: hingeHeight,
            overCutDoor: cutDown?.overCutDoor ? genericDistanceParser(cutDown.overCutDoor).universalValue() : 0,
            underCutDoor: cutDown?.underCutDoor ? genericDistanceParser(cutDown.underCutDoor).universalValue() : 0,
            underCutFrame: cutDown?.underCutFrame ? genericDistanceParser(cutDown.underCutFrame).universalValue() : 0,
        });

        out.push({
            openingId: openingId,
            name: openingName,
            manager: manager,
            isPair: isPair,
            pairSplitKey: pairSplitKey,
            hasDeadbolt: frameNeedsDeadbolt || doorNeedsDeadbolt,
            hasStrike: frameNeedsStrike || doorNeedsStrike,
            has161: frameHas161 || doorHas161,
            cutDown: cutDown,
        })
    })

    return out;
}

type PairSplitKey = "none" | "equal" | "active-larger" | "inactive-larger";

export function HardwarePrepTable(props: {
    hasDoors: boolean;
    hasFrames: boolean;
    hardwareDetail: HardwareDetailItem[]
    onReady(): void
}) {
    const hasAnyDeadboltRef = useRef(false);

    let rows = useMemo(() => {
        const out = props.hardwareDetail;

        out.map(o => {
            if(o.hasDeadbolt) {
                hasAnyDeadboltRef.current = true;
            }
        })

        function makeKey(input: InternalValue) {
            if(!input.enabled) return "false";
            return input.actualValue.toString();
        }

        const grp = groupByArr(out, v => [
            v.manager.pair.toString(),
            v.hasDeadbolt.toString(),
            v.hasStrike.toString(),
            v.has161.toString(),
            makeKey(v.manager.values.a),
            makeKey(v.manager.values.aStar),
            makeKey(v.manager.values.b),
            makeKey(v.manager.values.c),
            makeKey(v.manager.values.d),
            makeKey(v.manager.values.e),
            v.hasDeadbolt ? makeKey(v.manager.values.dbf) : "",
            v.hasDeadbolt ? makeKey(v.manager.values.dbfStar) : "",
            v.hasStrike || v.has161 ? makeKey(v.manager.values.clf) : "",
            v.hasStrike || v.has161 ? makeKey(v.manager.values.clfStar) : "",
            v.hasStrike || v.has161 ? makeKey(v.manager.values.cld) : "",
            v.hasStrike || v.has161 ? makeKey(v.manager.values.cldStar): "",
            v.hasDeadbolt ? makeKey(v.manager.values.dbd) : "",
            v.hasDeadbolt ? makeKey(v.manager.values.dbdStar) : "",
            makeKey(v.manager.values.bs),
            v.prepDiagramName || "",
        ].join(","))

        const rows = grp.map(v => {
            return v.values
        });

        let rows2: HardwareDetailItem[][] = [];

        rows.forEach(r => {
            for(let i = 0; i < r.length; i += 5) {
                rows2.push(r.slice(i, i + 5));
            }
        })

        return rows2;
    }, [props.hardwareDetail]);

    // must happen after all the useMemo/useState/useRef b/c it's modified there.
    // yes, I know, this could be designed better.
    const hasAnyDeadbolt = hasAnyDeadboltRef.current;

    let hasD = false;
    let hasE = false;
    let hasBS = false;
    let hasPrepDiagram = false;

    rows.map(r => {
        r.map((v: HardwareDetailItem) => {
            if(v.prepDiagramName) {
                hasPrepDiagram = true;
            }

            if(v.manager.values.d.enabled) {
                hasD = true;
            }

            if(v.manager.values.e.enabled) {
                hasE = true;
            }

            if(v.manager.values.bs.enabled) {
                hasBS = true;
            }
        })
    })

    const isBlank = rows.length === 0;
    const onReady = props.onReady;
    useEffect(() => {
        if(isBlank) onReady();
    }, [onReady, isBlank]);

    if(isBlank) {
        return null;
    }

    return (
        <PageRenderProvider key="hw-page-render" debugKey="hw-page" data={rows} onReady={props.onReady}>
            {(info: DataSplitInfo<HardwareDetailItem[]>) => (<Page orientation="landscape" key={info.pageIndex.toString()}>
                    <div style={{flex: 1, display: "flex", flexDirection: "column", alignItems: "stretch"}}>
                    <PDFHeader noAddress title="Hardware Prep Table" />
                    <table className={table}>
                        <thead>
                        <tr>
                            <th>Opening</th>
                            <th>Qty</th>
                            <th>Type</th>
                            <th>A</th>
                            <th>A*</th>
                            <th>B</th>
                            <th>C</th>
                            {hasD && <th>D</th>}
                            {hasE && <th>E</th>}
                            {props.hasFrames && hasAnyDeadbolt && <th>DBF</th>}
                            {props.hasFrames && hasAnyDeadbolt && <th>DBF*</th>}
                            {props.hasFrames && <th>CLF</th>}
                            {props.hasFrames && <th>CLF*</th>}
                            {props.hasDoors && <th>CLD</th>}
                            {props.hasDoors && <th>CLD*</th>}
                            {props.hasDoors && hasAnyDeadbolt && <th>DBD</th>}
                            {props.hasDoors && hasAnyDeadbolt && <th>DBD*</th>}
                            {hasBS && <th>BS</th>}
                            {hasPrepDiagram && <th>Diagram</th>}
                        </tr>
                        </thead>
                        <tbody>
                        {info.rows.map((items, index) => {
                            const r = items[0];

                            return (<tr key={r.name + "-" + index}>
                                <td>
                                    <div style={{maxWidth: 200, display: "flex", flexWrap: "wrap"}}>
                                        {items.map(i =>
                                            <a style={{margin: 2}}
                                               href="#"
                                                id={poOpeningDetailLink(i.openingId, "preps")}>{i.name} </a>
                                        )}
                                    </div>
                                </td>
                                <td style={{textAlign: "right"}}>
                                    {items.length}
                                </td>
                                <td>{r.manager.pair ? "Pair" : "Single"}</td>
                                <ValueCell value={r.manager.values.a}/>
                                <ValueCell value={r.manager.values.aStar}/>
                                <ValueCell value={r.manager.values.b}/>
                                <ValueCell value={r.manager.values.c}/>
                                {hasD && <ValueCell value={r.manager.values.d}/>}
                                {hasE && <ValueCell value={r.manager.values.e}/>}
                                {props.hasFrames && hasAnyDeadbolt && <ValueCell value={r.manager.values.dbf} disabled={r.manager.pair || !r.hasDeadbolt}/>}
                                {props.hasFrames && hasAnyDeadbolt && <ValueCell value={r.manager.values.dbfStar} disabled={r.manager.pair || !r.hasDeadbolt}/>}
                                {props.hasFrames && <ValueCell value={r.manager.values.clf} disabled={r.manager.pair || (!r.hasStrike && !r.has161)}/>}
                                {props.hasFrames && <ValueCell value={r.manager.values.clfStar} disabled={r.manager.pair || (!r.hasStrike && !r.has161)}/>}
                                {props.hasDoors && <ValueCell value={r.manager.values.cld} disabled={!r.hasStrike && !r.has161}/>}
                                {props.hasDoors && <ValueCell value={r.manager.values.cldStar} disabled={!r.hasStrike && !r.has161}/>}
                                {props.hasDoors && hasAnyDeadbolt && <ValueCell value={r.manager.values.dbd} disabled={!r.hasDeadbolt}/>}
                                {props.hasDoors && hasAnyDeadbolt && <ValueCell value={r.manager.values.dbdStar} disabled={!r.hasDeadbolt}/>}
                                {hasBS && <ValueCell value={r.manager.values.bs}/>}
                                {hasPrepDiagram && <td>
                                    {r.prepDiagramName && <a href={"#" + poPrepDiagram(r.prepDiagramName)}>{r.prepDiagramName}</a>}
                                </td>}
                            </tr>)
                        })}
                        </tbody>
                    </table>
                </div>
                </Page>
            )}
        </PageRenderProvider>

    )
}

export function poPrepDiagram(name: string) {
    return "prep-diagram-" + name;
}

export function ValueCell(props: {
    value: InternalValue;
    disabled?: boolean;
}) {

    if(props.disabled || !props.value.enabled) {
        return <td></td>
    }

    if(props.value.value !== undefined && props.value.value !== props.value.cValue || props.value.displayAsOverride) {
        return <td style={{backgroundColor: grey["200"], color: "black", textDecoration: "underline", fontWeight: "bold"}}>{ImperialDistance.showInches(props.value.actualValue)}</td>
    }

    return <td style={{color: grey["700"]}}>{ImperialDistance.showInches(props.value.actualValue)}</td>
}